import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, nullable } from './common.js';
import { ENGAGEMENT_TARGET_KINDS } from './enums/content.js';
import { ConcernTypeSchema, NotificationChannelSchema } from './enums/notifications.js';
import { AdminPageQuerySchema, adminPage } from './pagination.js';
/*
 * Notification concerns and templates (A-09, A-10) and the fan's notification preferences (M-08):
 * 04-data-models.md § Notifications, 05-api-specification.md admin `/notification-concerns*`,
 * `/notification-templates*` and app `/me/notification-preferences`. Bulk campaigns, the inbox and
 * push arrive in BACKEND-17 and reuse the template text and `deepLink` defined here.
 */
export const CONCERN_NAME_MAX_LENGTH = 40;
/** Push title limit (04 § notification_templates). */
export const TEMPLATE_SUBJECT_MAX_LENGTH = 65;
export const TEMPLATE_BODY_MAX_LENGTH = 240;
/** Placeholders a template may use; anything else in `{{…}}` is rejected. */
export const NOTIFICATION_PLACEHOLDERS = ['firstName'];
const PLACEHOLDER_PATTERN = /\{\{\s*([^{}]*?)\s*\}\}/g;
/** The `{{name}}` placeholders in `text` that are not in `NOTIFICATION_PLACEHOLDERS`. */
export function unknownPlaceholders(text) {
    const known = new Set(NOTIFICATION_PLACEHOLDERS);
    return [...text.matchAll(PLACEHOLDER_PATTERN)]
        .map((match) => match[1] ?? '')
        .filter((name) => !known.has(name));
}
/** Fills `{{firstName}}` (admin preview, BACKEND-17 dispatch). Unknown placeholders stay as typed. */
export function renderNotificationText(text, values) {
    return text.replace(PLACEHOLDER_PATTERN, (whole, name) => name in values ? values[name] : whole);
}
/**
 * Template deep links: an app path the fan lands on when tapping the notification (agreed with
 * the mobile track; BACKEND-17 puts it in the push payload as `deepLink`). The app opens it as
 * `lions://<path without the leading slash>` and sends unknown paths to the inbox.
 */
export const NOTIFICATION_DEEP_LINK_ROUTES = [
    { path: '/', label: 'Home', example: '/' },
    { path: '/event', label: 'Events tab', example: '/event' },
    { path: '/reels', label: 'Reels tab', example: '/reels' },
    { path: '/stadium', label: 'Stadium tab', example: '/stadium' },
    { path: '/teams', label: 'Teams tab', example: '/teams' },
    { path: '/notifications', label: 'Notification inbox', example: '/notifications' },
    { path: '/profile', label: 'Profile', example: '/profile' },
    {
        path: '/c/<kind>/<id>',
        label: 'One video, reel, article, gallery or event (same path as share links)',
        example: '/c/event/66f0c0ffee0000000000abcd',
    },
];
const STATIC_DEEP_LINKS = new Set(NOTIFICATION_DEEP_LINK_ROUTES.map((route) => route.path).filter((path) => !path.includes('<')));
const ITEM_DEEP_LINK = new RegExp(`^/c/(${ENGAGEMENT_TARGET_KINDS.map((kind) => kind.toLowerCase()).join('|')})/[0-9a-f]{24}$`);
export const isNotificationDeepLink = (path) => STATIC_DEEP_LINKS.has(path) || ITEM_DEEP_LINK.test(path);
export const NotificationDeepLinkSchema = z
    .string()
    .trim()
    .refine(isNotificationDeepLink, {
    message: `Must be an app route: ${NOTIFICATION_DEEP_LINK_ROUTES.map((r) => r.path).join(', ')}`,
});
const templateText = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .superRefine((text, ctx) => {
    for (const name of unknownPlaceholders(text)) {
        ctx.addIssue({
            code: 'custom',
            message: `Unknown placeholder {{${name}}}; use ${NOTIFICATION_PLACEHOLDERS.map((p) => `{{${p}}}`).join(', ')}`,
        });
    }
});
// Concerns
export const NotificationConcernSchema = z
    .object({
    id: IdSchema,
    name: z.string(),
    type: ConcernTypeSchema,
    /** Q-24: marketing concerns stay muted for a fan until they opt in. */
    isMarketing: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .meta({ id: 'NotificationConcern' });
/** `GET /v1/admin/notification-concerns?type&page&limit`: newest first. */
export const NotificationConcernListQuerySchema = AdminPageQuerySchema.extend({
    type: ConcernTypeSchema.optional(),
});
export const NotificationConcernPageSchema = adminPage(NotificationConcernSchema).meta({
    id: 'NotificationConcernPage',
});
/** `POST /v1/admin/notification-concerns`: the name is stored upper case and unique. */
export const NotificationConcernCreateRequestSchema = z
    .strictObject({
    name: z
        .string()
        .trim()
        .min(1)
        .max(CONCERN_NAME_MAX_LENGTH)
        .regex(/^[A-Za-z0-9 _-]+$/, 'Use letters, digits, spaces, _ or -'),
    /** The source offers Custom only; SYSTEM is reserved for platform messages. */
    type: z.literal('CUSTOM').default('CUSTOM'),
    isMarketing: z.boolean().default(false),
})
    .meta({ id: 'NotificationConcernCreateRequest' });
// Templates
/** The concern shown on a template row. */
export const NotificationTemplateConcernSchema = z
    .object({ id: IdSchema, name: z.string(), isMarketing: z.boolean() })
    .meta({ id: 'NotificationTemplateConcern' });
export const NotificationTemplateSchema = z
    .object({
    id: IdSchema,
    concern: NotificationTemplateConcernSchema,
    channel: NotificationChannelSchema,
    subject: z.string(),
    body: z.string(),
    deepLink: nullable(z.string()),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .meta({ id: 'NotificationTemplate' });
/** `GET /v1/admin/notification-templates?concernId&page&limit`: newest first. */
export const NotificationTemplateListQuerySchema = AdminPageQuerySchema.extend({
    concernId: IdSchema.optional(),
});
export const NotificationTemplatePageSchema = adminPage(NotificationTemplateSchema).meta({
    id: 'NotificationTemplatePage',
});
const TemplateFields = {
    concernId: IdSchema,
    channel: NotificationChannelSchema.default('IN_APP'),
    subject: templateText(TEMPLATE_SUBJECT_MAX_LENGTH),
    body: templateText(TEMPLATE_BODY_MAX_LENGTH),
    /** Optional app route (`NOTIFICATION_DEEP_LINK_ROUTES`); `null` clears it. */
    deepLink: NotificationDeepLinkSchema.nullable().optional(),
};
/** `POST /v1/admin/notification-templates`. */
export const NotificationTemplateCreateRequestSchema = z
    .strictObject(TemplateFields)
    .meta({ id: 'NotificationTemplateCreateRequest' });
/** `PATCH /v1/admin/notification-templates/{id}`: any subset. */
export const NotificationTemplateUpdateRequestSchema = z
    .strictObject({
    concernId: TemplateFields.concernId.optional(),
    subject: TemplateFields.subject.optional(),
    body: TemplateFields.body.optional(),
    deepLink: TemplateFields.deepLink,
})
    .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'Change at least one field',
})
    .meta({ id: 'NotificationTemplateUpdateRequest' });
// App preferences
export const NotificationConcernPreferenceSchema = z
    .object({
    id: IdSchema,
    name: z.string(),
    isMarketing: z.boolean(),
    /** Marketing concerns are muted until the fan opts in (Q-24). */
    muted: z.boolean(),
})
    .meta({ id: 'NotificationConcernPreference' });
/** `GET` and `PUT /v1/app/me/notification-preferences`: every live concern, by name. */
export const NotificationPreferencesSchema = z
    .object({
    pushEnabled: z.boolean(),
    concerns: z.array(NotificationConcernPreferenceSchema),
})
    .meta({ id: 'NotificationPreferences' });
/** `PUT /v1/app/me/notification-preferences`: only the listed concerns change. */
export const NotificationPreferencesUpdateRequestSchema = z
    .strictObject({
    pushEnabled: z.boolean().optional(),
    concerns: z
        .array(z.strictObject({ id: IdSchema, muted: z.boolean() }))
        .max(100)
        .optional(),
})
    .refine((body) => body.pushEnabled !== undefined || body.concerns !== undefined, {
    message: 'Send pushEnabled or concerns',
})
    .meta({ id: 'NotificationPreferencesUpdateRequest' });
//# sourceMappingURL=notifications.js.map